import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { SettingsService } from 'src/app/shared/services/settings';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { ChannelSetupService, firstValidationMessage } from 'src/app/shared/services/channel-setup/channel-setup.service';
import { createHostedEmailPreviewUrl } from '../../email-preview-url';
import { CatalogTemplate, CatalogTemplateChannel, CatalogTemplateActor, CatalogTemplateUsedFor, ChannelSetupConfig } from 'src/app/shared/services/channel-setup/channel-setup.models';
import { LANGUAGE_OPTIONS, languageShortCode } from '../../channel-languages';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

const EMAIL_IN_TEXT = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const EMAIL_PREVIEW_CSP = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src * data: https:; style-src \'unsafe-inline\'; font-src *; script-src \'none\'">';

interface CatalogForm {
  id: string;
  template_id: string;
  template_name: string;
  template_title: string;
  template_language: string;
  template_used_for: CatalogTemplateUsedFor;
  template_text: string;
  sender_id: string;
  principal_entity_id: string;
  template_unicode: boolean;
  email_subject: string;
  email_from: string;
  template_image: string;
  is_active: boolean;
  html: string;
  file_name: string;
  update_log: CatalogTemplateActor[];
}

@Component({
  selector: 'app-channel-templates',
  templateUrl: './channel-templates.component.html',
  styleUrls: ['./channel-templates.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, LtmsLoaderComponent],
})
export class ChannelTemplatesComponent implements OnInit, OnDestroy {
  @Input() isWriteAllowed = false;

  readonly channels: Array<{ key: CatalogTemplateChannel; label: string }> = [
    { key: 'sms', label: 'SMS' },
    { key: 'email', label: 'Email' },
    { key: 'whatsapp', label: 'WhatsApp' },
  ];
  readonly languageOptions = LANGUAGE_OPTIONS;
  templateInUse = false;
  templateUsageChecked = false;
  fromSuggestions: string[] = [];
  private inUseIds = new Set<string>();
  readonly usedForOptions: Array<{ key: CatalogTemplateUsedFor; label: string; hint: string }> = [
    { key: 'on_demand_request', label: 'On demand request', hint: 'Ad-hoc sends. This is the service in use today.' },
    { key: 'customer_notifier', label: 'Customer Notifier', hint: 'Invites and reminders (later)' },
    { key: 'action_service', label: 'Action Service', hint: 'Issue and action emails (later)' },
    { key: 'other', label: 'Other', hint: 'Anything else (later)' },
  ];
  readonly mergeFields = [
    { token: '--customerName--', label: 'Customer name' },
    { token: '--feedbackUrl--', label: 'Feedback link' },
    { token: '--shopName--', label: 'Touchpoint' },
    { token: '--brandName--', label: 'Brand' },
  ];

  activeChannel: CatalogTemplateChannel = 'sms';
  brandId: string | null = null;
    loading = false;
  saving = false;
  loadError = '';
  rows: CatalogTemplate[] = [];
  editorOpen = false;
  emailPreviewOpen = false;
  emailPreviewSubject = '';
  emailPreviewFrom = '';
  emailPreviewUrl: SafeResourceUrl | null = null;
  private emailPreviewObjectUrl = '';
  confirmDelete: CatalogTemplate | null = null;
  confirmSave: { title: string; message: string } | null = null;
  pageNumber = 1;
  readonly pageSize = 10;
  form: CatalogForm = this.emptyForm();
  private savedForm: CatalogForm = this.emptyForm();
  fieldErrors: Record<string, string> = {};
  private showErrors = false;
  readonly fieldMax = {
    title: 80,
    name: 120,
    id: 80,
    sender: 20,
    entity: 20,
    text: 2000,
    subject: 255,
    from: 255,
    image: 500,
  };
  private fileInput: HTMLInputElement | null = null;

  constructor(
    private readonly appStateService: AppStateService,
    private readonly channelSetupService: ChannelSetupService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly host: ElementRef<HTMLElement>,
    private readonly sanitizer: DomSanitizer,
    private readonly settingsService: SettingsService,
  ) {
    effect(() => {
      if (!this.appStateService.initialized()) {
        return;
      }
      const authDetails: any = this.appStateService.managerAccess();
      const loggedIn = authDetails?.loggedInManager ?? {};
      const brandDetails: any = this.appStateService.brandDetails();
      const nextBrandId = loggedIn.brand_id || brandDetails?._id || null;
      if (nextBrandId && nextBrandId !== this.brandId) {
        this.brandId = nextBrandId;
        this.load();
        this.settingsService.getFeedbackConfiguration(nextBrandId).subscribe({
          next: (response) => {
            const configuration = response?.data?.brand_configuration?.[0] || {};
            const languages = configuration.languages && typeof configuration.languages === 'object'
              ? configuration.languages
              : {};
            const order = Array.isArray(configuration.languages_order) && configuration.languages_order.length
              ? configuration.languages_order
              : Object.keys(languages);
            order.forEach((key: unknown) => {
              const value = String(key || '').trim();
              if (!value || !Object.prototype.hasOwnProperty.call(languages, value)) {
                return;
              }
              if (LANGUAGE_OPTIONS.some((lang) => lang.key === value)) {
                return;
              }
              LANGUAGE_OPTIONS.unshift({
                key: value,
                name: value,
                native_name: value,
                short: languageShortCode(value),
              });
            });
            this.changeDetector.markForCheck();
          },
        });
      }
    });
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.onUnauthorizedClick, true);
    this.applyTemplateQuery();
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.onUnauthorizedClick, true);
    this.fileInput = null;
    this.closeEmailPreview();
  }

  private readonly onUnauthorizedClick = (event: Event): void => {
    if (this.isWriteAllowed) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.is-unauthorized')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toastService.showPopup('ERROR', 'Not authorized', 'Not authorized', 15000);
  };

  private applyTemplateQuery(): void {
    const params = this.route.snapshot.queryParamMap;
    const channel = String(params.get('template_channel') || '').toLowerCase() as CatalogTemplateChannel;
    if (channel === 'sms' || channel === 'email' || channel === 'whatsapp') {
      this.activeChannel = channel;
      if (this.brandId) {
        this.load();
      }
    }
    if (params.get('create_template') === '1') {
      setTimeout(() => {
        this.openCreate();
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { create_template: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });
    }
  }

  setChannel(channel: CatalogTemplateChannel): void {
    if (this.activeChannel === channel) {
      return;
    }
    this.activeChannel = channel;
    this.pageNumber = 1;
    this.closeEditor();
    this.load();
  }

  get pagedRows(): CatalogTemplate[] {
    const start = (this.pageNumber - 1) * this.pageSize;
    return this.rows.slice(start, start + this.pageSize);
  }

  get totalCount(): number {
    return this.rows.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get rangeStart(): number {
    if (!this.totalCount) {
      return 0;
    }
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalCount);
  }

  get hasPrevPage(): boolean {
    return this.pageNumber > 1;
  }

  get hasNextPage(): boolean {
    return this.pageNumber < this.totalPages && this.totalCount > 0;
  }

  pageMove(direction: 'previous' | 'next'): void {
    if (direction === 'previous' && this.hasPrevPage) {
      this.pageNumber -= 1;
    }
    if (direction === 'next' && this.hasNextPage) {
      this.pageNumber += 1;
    }
  }

  goToFirstPage(): void {
    this.pageNumber = 1;
  }

  goToLastPage(): void {
    this.pageNumber = this.totalPages;
  }

  usedForLabel(key?: string): string {
    return this.usedForOptions.find((item) => item.key === key)?.label || 'On demand request';
  }

  get usedForHint(): string {
    return this.usedForOptions.find((item) => item.key === this.form.template_used_for)?.hint || '';
  }

  createdOn(row: CatalogTemplate): string | Date | null {
    return row.created_on || row.created_by?.updated_date || null;
  }

  createdBy(row: CatalogTemplate): string {
    const actor = row.created_by;
    if (!actor) {
      return '—';
    }
    return actor.name || actor.email_address || '—';
  }

  createdByTitle(row: CatalogTemplate): string {
    const actor = row.created_by;
    if (!actor) {
      return '';
    }
    return [actor.name, actor.email_address].filter(Boolean).join(' · ');
  }

  fieldError(key: string): string {
    return this.fieldErrors[key] || '';
  }

  hasError(key: string): boolean {
    return Boolean(this.fieldErrors[key]);
  }

  isFormDirty(key: keyof CatalogForm): boolean {
    const current = this.form[key];
    const saved = this.savedForm[key];
    if (typeof current === 'boolean' || typeof saved === 'boolean') {
      return Boolean(current) !== Boolean(saved);
    }
    return String(current ?? '') !== String(saved ?? '');
  }

  get formDirty(): boolean {
    return (
      this.isFormDirty('template_id')
      || this.isFormDirty('template_title')
      || this.isFormDirty('template_language')
      || this.isFormDirty('template_used_for')
      || this.isFormDirty('template_text')
      || this.isFormDirty('sender_id')
      || this.isFormDirty('principal_entity_id')
      || this.isFormDirty('template_unicode')
      || this.isFormDirty('email_subject')
      || this.isFormDirty('email_from')
      || this.isFormDirty('template_image')
      || this.isFormDirty('is_active')
      || this.isFormDirty('html')
      || this.isFormDirty('file_name')
    );
  }

  discardChanges(): void {
    if (!this.formDirty) {
      return;
    }
    this.form = {
      ...this.savedForm,
      update_log: [...(this.savedForm.update_log || [])],
    };
    this.resetFieldErrors();
    this.changeDetector.markForCheck();
  }

  private snapshotForm(): void {
    this.savedForm = {
      ...this.form,
      update_log: [...(this.form.update_log || [])],
    };
  }

  onFieldChange(): void {
    if (this.showErrors) {
      this.fieldErrors = this.collectErrors();
    }
  }

  setUsedFor(value: CatalogTemplateUsedFor): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form.template_used_for = value;
    this.onFieldChange();
  }

  setActive(next: boolean): void {
    if (!this.isWriteAllowed) {
      return;
    }
    if (!next && this.templateInUse) {
      this.form.is_active = true;
      this.toastService.showPopup('ERROR', 'Template in use', 'This template is used in channel setup, so it stays active.', 15000);
      this.changeDetector.markForCheck();
      return;
    }
    this.form.is_active = next;
    this.onFieldChange();
  }

  useFromSuggestion(value: string): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form.email_from = value;
    this.onFieldChange();
  }

  languageChoices(): Array<{ key: string; name: string; native_name: string }> {
    return this.languageOptions;
  }

  editTip(): string {
    if (!this.isWriteAllowed) {
      return 'You are not allowed to edit this template.';
    }
    return 'Edit';
  }

  deleteTip(row: CatalogTemplate): string {
    if (!this.isWriteAllowed) {
      return 'You are not allowed to delete this template.';
    }
    if (this.isTemplateInUse(row)) {
      return 'This template is in use, so it cannot be deleted.';
    }
    return 'Delete';
  }

  lockTip(field: 'usedFor' | 'language' | 'active'): string | null {
    if (!this.isWriteAllowed) {
      return 'You are not allowed to edit this template.';
    }
    if (field === 'active') {
      return this.templateInUse && this.form.is_active
        ? 'This template is in use, so it stays active.'
        : null;
    }
    if (!this.form.id) {
      return null;
    }
    return field === 'language'
      ? 'Language cannot be changed.'
      : 'Template used for cannot be changed.';
  }

  displayTitle(row: { template_title?: string; template_name?: string }): string {
    return (row.template_title || row.template_name || '').trim() || '—';
  }

  copyTemplateId(value: string, event?: Event, label = 'Template Id'): void {
    event?.preventDefault();
    event?.stopPropagation();
    const text = String(value || '').trim();
    if (!text) {
      return;
    }
    if (!navigator.clipboard?.writeText) {
      this.toastService.showPopup('ERROR', 'Copy failed', `Could not copy ${label}.`, 15000);
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      this.toastService.showPopup('SUCCESS', 'Copied', `${label} copied.`, 3000);
    }).catch(() => {
      this.toastService.showPopup('ERROR', 'Copy failed', `Could not copy ${label}.`, 15000);
    });
  }

  get historyNewest(): CatalogTemplateActor[] {
    return [...(this.form.update_log || [])].reverse();
  }

  load(): void {
    if (!this.brandId) {
      return;
    }
    this.loading = true;
    this.loadError = '';
    this.channelSetupService.listCatalogTemplates(this.brandId, this.activeChannel).subscribe({
      next: (rows) => {
        rows.forEach((row) => {
          const value = String(row.template_language || '').trim();
          if (!value || LANGUAGE_OPTIONS.some((lang) => lang.key === value)) {
            return;
          }
          LANGUAGE_OPTIONS.unshift({
            key: value,
            name: value,
            native_name: value,
            short: languageShortCode(value),
          });
        });
        this.rows = rows;
        this.pageNumber = 1;
        this.loading = false;
        this.refreshInUseTemplates();
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.loading = false;
        this.loadError = firstValidationMessage(error) || error?.error?.message || error?.message || 'Could not load templates';
        this.changeDetector.markForCheck();
      },
    });
  }

  openCreate(): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form = this.emptyForm();
    this.snapshotForm();
    this.resetFieldErrors();
    this.templateInUse = false;
    this.templateUsageChecked = false;
    this.editorOpen = true;
    this.loadEmailSetupHints();
  }

  openEdit(row: CatalogTemplate): void {
    const value = String(row.template_language || '').trim();
    if (value && !LANGUAGE_OPTIONS.some((lang) => lang.key === value)) {
      LANGUAGE_OPTIONS.unshift({
        key: value,
        name: value,
        native_name: value,
        short: languageShortCode(value),
      });
    }
    this.form = {
      id: row.id,
      template_id: row.template_id || '',
      template_name: row.template_name || '',
      template_title: row.template_title || row.template_name || '',
      template_language: row.template_language || '',
      template_used_for: (row.template_used_for || 'on_demand_request') as CatalogTemplateUsedFor,
      template_text: row.template_text || '',
      sender_id: row.sender_id || '',
      principal_entity_id: row.principal_entity_id || '',
      template_unicode: Boolean(row.template_unicode),
      email_subject: row.email_subject || '',
      email_from: row.email_from || '',
      template_image: row.template_image || '',
      is_active: row.is_active !== false,
      html: '',
      file_name: '',
      update_log: row.update_log || [],
    };
    this.snapshotForm();
    this.resetFieldErrors();
    this.templateInUse = false;
    this.templateUsageChecked = false;
    this.editorOpen = true;
    this.loadTemplateUsage(row);
    this.loadEmailSetupHints();
  }

  closeEditor(): void {
    this.editorOpen = false;
    this.resetFieldErrors();
  }

  canReadEmailHtml(row?: CatalogTemplate): boolean {
    if (row) {
      return Boolean(String(row.template_id || '').trim());
    }
    return Boolean(this.form.html.trim() || String(this.form.template_id || '').trim());
  }

  viewEmailHtml(row?: CatalogTemplate): void {
    this.loadEmailHtml(row, (html) => {
      this.openHtmlInNewTab(this.wrapEmailDocument(this.decodeEmailHtml(html)));
    });
  }

  previewEmailHtml(row?: CatalogTemplate): void {
    this.loadEmailHtml(row, (html) => {
      this.showEmailPreview(
        html,
        row?.email_subject || this.form.email_subject,
        row?.email_from || this.form.email_from,
      );
    });
  }

  downloadEmailHtml(row?: CatalogTemplate): void {
    this.loadEmailHtml(row, (html, fileName) => {
      this.saveHtmlFile(html, fileName);
    });
  }

  closeEmailPreview(): void {
    this.emailPreviewOpen = false;
    this.revokeEmailPreview();
    this.emailPreviewSubject = '';
    this.emailPreviewFrom = '';
  }

  private loadEmailHtml(row: CatalogTemplate | undefined, next: (html: string, fileName: string) => void): void {
    const inline = !row ? this.form.html.trim() : '';
    const catalogId = String(row?.template_id || (inline ? '' : this.form.template_id) || '').trim();
    const fileId = String((row as CatalogTemplate & { gridfs_id?: string })?.gridfs_id || '').trim();
    const fileName = row?.template_name || this.form.file_name || this.form.template_name || 'email-template';
    if (inline) {
      next(inline, fileName);
      return;
    }
    if (!catalogId && !fileId) {
      this.toastService.showPopup('ERROR', 'No template', 'There is no HTML template yet.', 15000);
      return;
    }
    if (!this.brandId) {
      this.toastService.showPopup('ERROR', 'No template', 'Could not load the email template', 15000);
      return;
    }
    const apply = (template: { html?: string; file_name?: string } | null) => {
      const body = String(template?.html || '').trim();
      if (!body) {
        this.toastService.showPopup('ERROR', 'Empty template', 'The saved HTML template is empty.', 15000);
        return;
      }
      next(body, template?.file_name || fileName);
    };
    const loadRaw = (id: string) => this.channelSetupService.getInviteEmailTemplate(this.brandId as string, id);
    const loadId = fileId || catalogId;
    loadRaw(loadId).subscribe({
      next: (template) => apply(template),
      error: () => {
        if (!catalogId || catalogId === loadId) {
          this.toastService.showPopup('ERROR', 'Could not load template', 'Could not load the email template', 15000);
          return;
        }
        loadRaw(catalogId).subscribe({
          next: (template) => apply(template),
          error: (error) => {
            this.toastService.showPopup(
              'ERROR',
              'Could not load template',
              error?.error?.message || error?.message || 'Could not load the email template',
              15000,
            );
          },
        });
      },
    });
  }

  private showEmailPreview(html: string, subject: string, from: string): void {
    this.closeEmailPreview();
    const rendered = this.wrapEmailDocument(this.applySampleTokens(this.decodeEmailHtml(html)));
    if (!rendered) {
      this.toastService.showPopup('ERROR', 'Empty template', 'The HTML template is empty.', 15000);
      return;
    }
    this.emailPreviewSubject = this.applySampleTokens(subject || '');
    this.emailPreviewFrom = from || '';
    this.emailPreviewOpen = true;
    void this.assignPreviewFrame(rendered);
  }

  private applySampleTokens(value: string): string {
    const samples: Record<string, string> = {
      '--feedbackUrl--': 'https://feedback.example.com/sample',
      '--unsubscribeUrl--': 'https://feedback.example.com/unsubscribe',
    };
    let next = value || '';
    Object.keys(samples).forEach((token) => {
      const name = token.replace(/--/g, '');
      next = next.split(token).join(samples[token]);
      next = next.split(`{{${name}}}`).join(samples[token]);
    });
    return next;
  }

  private decodeEmailHtml(html: string): string {
    const raw = html || '';
    if (/&lt;([a-z!/])/i.test(raw) && !/<[a-z!/]/i.test(raw)) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = raw;
      return textarea.value;
    }
    return raw;
  }

  private wrapEmailDocument(html: string): string {
    const body = html.trim();
    if (!body) {
      return '';
    }
    const viewport = '<meta name="viewport" content="width=device-width, initial-scale=1">';
    let next = body;
    if (/<html[\s>]/i.test(body) || /<!doctype/i.test(body)) {
      if (!/name=["']viewport["']/i.test(body)) {
        if (/<head[^>]*>/i.test(body)) {
          next = body.replace(/<head([^>]*)>/i, `<head$1>${viewport}`);
        } else {
          next = body.replace(/<html([^>]*)>/i, `<html$1><head>${viewport}</head>`);
        }
      }
    } else {
      next = `<!DOCTYPE html><html><head><meta charset="utf-8">${viewport}<base target="_blank"></head><body>${body}</body></html>`;
    }
    if (/http-equiv=["']Content-Security-Policy["']/i.test(next)) {
      return next;
    }
    if (/<head[^>]*>/i.test(next)) {
      return next.replace(/<head([^>]*)>/i, `<head$1>${EMAIL_PREVIEW_CSP}`);
    }
    return `${EMAIL_PREVIEW_CSP}\n${next}`;
  }

  private revokeEmailPreview(): void {
    if (this.emailPreviewObjectUrl) {
      URL.revokeObjectURL(this.emailPreviewObjectUrl);
      this.emailPreviewObjectUrl = '';
    }
    this.emailPreviewUrl = null;
  }

  private async assignPreviewFrame(html: string): Promise<void> {
    this.revokeEmailPreview();
    try {
      const url = await createHostedEmailPreviewUrl(html);
      this.emailPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } catch {
      this.emailPreviewObjectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      this.emailPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.emailPreviewObjectUrl);
    }
    this.changeDetector.markForCheck();
  }

  private openHtmlInNewTab(html: string): void {
    if (!html.trim()) {
      this.toastService.showPopup('ERROR', 'Empty template', 'The HTML template is empty.', 15000);
      return;
    }
    const win = window.open('about:blank', '_blank');
    if (!win) {
      this.toastService.showPopup('ERROR', 'Popup blocked', 'Allow popups to view the HTML template in a new tab.', 15000);
      return;
    }
    void this.loadEmailHtmlWindow(html, win);
  }

  private async loadEmailHtmlWindow(html: string, win: Window): Promise<void> {
    const wrapped = this.wrapEmailDocument(html);
    try {
      const url = await createHostedEmailPreviewUrl(wrapped);
      win.location.replace(url);
    } catch {
      const url = URL.createObjectURL(new Blob([wrapped], { type: 'text/html' }));
      win.location.replace(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  }

  private saveHtmlFile(html: string, fileName: string): void {
    const safeName = /\.html?$/i.test(fileName) ? fileName : `${fileName}.html`;
    const link = document.createElement('a');
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    link.href = url;
    link.download = safeName;
    link.click();
    URL.revokeObjectURL(url);
  }

  pickHtmlFile(input: HTMLInputElement): void {
    this.fileInput = input;
    input.click();
  }

  onHtmlFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.html')) {
      this.toastService.showPopup('ERROR', 'Invalid file', 'Upload an HTML email template (.html).', 15000);
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.form.html = String(reader.result || '');
      this.form.file_name = file.name;
      this.onFieldChange();
      this.changeDetector.markForCheck();
    };
    reader.readAsText(file);
    input.value = '';
  }

  insertToken(token: string): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form.template_text = `${this.form.template_text || ''}${token}`;
    this.onFieldChange();
  }

  save(): void {
    if (!this.brandId || !this.isWriteAllowed || this.saving || !this.formDirty) {
      return;
    }
    this.showErrors = true;
    this.fieldErrors = this.collectErrors();
    const firstError = Object.values(this.fieldErrors)[0];
    if (firstError) {
      this.toastService.showPopup('ERROR', 'Check the form', firstError, 15000);
      this.changeDetector.markForCheck();
      return;
    }
    const name = this.displayTitle(this.form);
    const channel = this.channelLabel(this.activeChannel);
    this.confirmSave = this.form.id
      ? {
        title: 'Save template changes?',
        message: `${name} will be updated in the ${channel} library. Continue?`,
      }
      : {
        title: 'Save this template?',
        message: `${name} will be added to the ${channel} library. Continue?`,
      };
  }

  cancelSaveConfirm(): void {
    this.confirmSave = null;
  }

  confirmSaveNow(): void {
    if (!this.confirmSave) {
      return;
    }
    this.confirmSave = null;
    this.persistSave();
  }

  private persistSave(): void {
    if (!this.brandId || !this.isWriteAllowed || this.saving) {
      return;
    }
    if (this.form.id && this.templateInUse && !this.form.is_active) {
      this.form.is_active = true;
      this.toastService.showPopup('ERROR', 'Template in use', 'This template is used in channel setup, so it stays active.', 15000);
      this.changeDetector.markForCheck();
      return;
    }
    const payload: Record<string, unknown> = {
      channel: this.activeChannel,
      template_name: this.form.id ? this.form.template_name.trim() : this.buildTemplateName(),
      template_title: this.form.template_title.trim(),
      template_language: this.form.template_language,
      template_used_for: this.form.template_used_for,
      is_active: this.form.is_active,
    };
    if (this.form.id) {
      payload['id'] = this.form.id;
    }
    if (this.activeChannel === 'email') {
      payload['email_subject'] = this.form.email_subject.trim();
      payload['email_from'] = this.form.email_from.trim();
      if (this.form.html) {
        payload['html'] = this.form.html;
        payload['file_name'] = this.form.file_name;
      } else if (this.form.template_id) {
        payload['template_id'] = this.form.template_id;
      }
    } else {
      payload['template_id'] = this.form.template_id.trim();
      payload['template_text'] = this.form.template_text;
      payload['sender_id'] = this.form.sender_id.trim();
    }
    if (this.activeChannel === 'sms') {
      payload['principal_entity_id'] = this.form.principal_entity_id.trim();
      payload['template_unicode'] = this.form.template_unicode;
    }
    if (this.activeChannel === 'whatsapp') {
      payload['template_image'] = this.form.template_image.trim();
    }

    this.saving = true;
    this.channelSetupService.saveCatalogTemplate(this.brandId, payload as { channel: CatalogTemplateChannel; id?: string }).subscribe({
      next: () => {
        this.saving = false;
        this.editorOpen = false;
        this.resetFieldErrors();
        this.toastService.showPopup('SUCCESS', 'Template saved', `${this.displayTitle(this.form)} is in the ${this.channelLabel(this.activeChannel)} library.`, 15000);
        this.load();
      },
      error: (error) => {
        this.saving = false;
        this.toastService.showPopup(
          'ERROR',
          'Cannot save',
          firstValidationMessage(error) || error?.error?.message || error?.message || 'Failed to save template',
          15000,
        );
        this.changeDetector.markForCheck();
      },
    });
  }

  askDelete(row: CatalogTemplate): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.lookupTemplateUsage(row, (inUse) => {
      if (inUse) {
        this.inUseIds.add(row.id);
        this.toastService.showPopup(
          'ERROR',
          'Cannot delete',
          `${this.displayTitle(row)} is used in channel setup, so it cannot be deleted.`,
          15000,
        );
        this.changeDetector.markForCheck();
        return;
      }
      this.confirmDelete = row;
      this.changeDetector.markForCheck();
    });
  }

  cancelDelete(): void {
    this.confirmDelete = null;
  }

  confirmDeleteNow(): void {
    const row = this.confirmDelete;
    this.confirmDelete = null;
    if (!row || !this.brandId || !this.isWriteAllowed) {
      return;
    }
    this.channelSetupService.deleteCatalogTemplate(this.brandId, this.activeChannel, row.id).subscribe({
      next: () => {
        this.toastService.showPopup('SUCCESS', 'Template deleted', `${this.displayTitle(row)} was removed from the library.`, 15000);
        this.load();
      },
      error: (error) => {
        this.toastService.showPopup(
          'ERROR',
          'Cannot delete',
          firstValidationMessage(error) || error?.error?.message || error?.message || 'Failed to delete template',
          15000,
        );
      },
    });
  }

  channelLabel(channel: CatalogTemplateChannel): string {
    return this.channels.find((item) => item.key === channel)?.label || channel;
  }

  private resetFieldErrors(): void {
    this.showErrors = false;
    this.fieldErrors = {};
  }

  private collectErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    const title = (this.form.template_title || '').trim();
    if (!title) {
      errors['template_title'] = 'Title is required.';
    } else if (title.length > this.fieldMax.title) {
      errors['template_title'] = `Title must be ${this.fieldMax.title} characters or less.`;
    } else if (!this.titleCode(title)) {
      errors['template_title'] = 'Title needs at least one letter or number.';
    }

    if (!this.form.template_used_for) {
      errors['template_used_for'] = 'Select what this template is used for.';
    }
    if (!this.form.template_language) {
      errors['template_language'] = 'Language is required.';
    }

    if (this.activeChannel === 'email') {
      const subject = (this.form.email_subject || '').trim();
      const from = (this.form.email_from || '').trim();
      if (!subject) {
        errors['email_subject'] = 'Email subject is required.';
      }
      if (!from) {
        errors['email_from'] = 'From address is required.';
      } else if (!EMAIL_IN_TEXT.test(from)) {
        errors['email_from'] = 'Enter the sender like this: LitmusWorld <feedback@litmusworld.net>.';
      }
      if (!this.form.id && !this.form.html.trim()) {
        errors['html'] = 'Upload an HTML file for a new email template.';
      }
      return errors;
    }

    const templateId = (this.form.template_id || '').trim();
    const senderId = (this.form.sender_id || '').trim();
    const text = (this.form.template_text || '').trim();
    if (!templateId) {
      errors['template_id'] = 'Template Id is required.';
    }
    if (!senderId) {
      errors['sender_id'] = 'Sender Id is required.';
    }
    if (!text) {
      errors['template_text'] = 'Template text is required.';
    }
    const entityId = (this.form.principal_entity_id || '').trim();
    if (this.activeChannel === 'sms' && entityId && !/^\d+$/.test(entityId)) {
      errors['principal_entity_id'] = 'Principal entity Id should contain digits only.';
    }
    const image = (this.form.template_image || '').trim();
    if (this.activeChannel === 'whatsapp' && image && !/^https?:\/\//i.test(image)) {
      errors['template_image'] = 'Image URL must start with http:// or https://.';
    }
    return errors;
  }

  isTemplateInUse(row: CatalogTemplate): boolean {
    return this.inUseIds.has(row.id);
  }

  private refreshInUseTemplates(): void {
    if (!this.brandId || !this.rows.length) {
      this.inUseIds = new Set();
      return;
    }
    this.lookupSetups((invites, backup) => {
      const next = new Set<string>();
      this.rows.forEach((row) => {
        if (this.configUsesTemplate(invites, row) || this.configUsesTemplate(backup, row)) {
          next.add(row.id);
        }
      });
      this.inUseIds = next;
      this.changeDetector.markForCheck();
    });
  }

  private loadTemplateUsage(row: CatalogTemplate): void {
    if (!this.brandId || !row.id) {
      this.templateUsageChecked = true;
      return;
    }
    this.lookupTemplateUsage(row, (inUse) => {
      this.templateInUse = inUse;
      this.templateUsageChecked = true;
      if (inUse) {
        this.inUseIds.add(row.id);
      }
      this.changeDetector.markForCheck();
    });
  }

  private lookupTemplateUsage(row: CatalogTemplate, done: (inUse: boolean) => void): void {
    if (!this.brandId || !row.id) {
      done(false);
      return;
    }
    this.lookupSetups((invites, backup) => {
      done(this.configUsesTemplate(invites, row) || this.configUsesTemplate(backup, row));
    });
  }

  private lookupSetups(done: (invites: ChannelSetupConfig | null, backup: ChannelSetupConfig | null) => void): void {
    if (!this.brandId) {
      done(null, null);
      return;
    }
    forkJoin({
      invites: this.channelSetupService.getConfig(this.brandId).pipe(catchError(() => of(null))),
      backup: this.channelSetupService.getOnDemand(this.brandId).pipe(catchError(() => of(null))),
    }).subscribe(({ invites, backup }) => done(invites, backup));
  }

  private configUsesTemplate(config: ChannelSetupConfig | null, row: CatalogTemplate): boolean {
    const ids = new Set(
      [row.id, row.template_id, row.template_name]
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    );
    if (!ids.size || !config?.attempts?.length) {
      return false;
    }
    return config.attempts.some((attempt) => {
      const bags = [attempt.templates?.sms, attempt.templates?.email, attempt.templates?.whatsapp];
      return bags.some((bag) => Object.values(bag || {}).some((tpl) => {
        const refs = [
          tpl.content_template_id,
          tpl.email_template_id,
          tpl.template_file_id,
          tpl.whatsapp_template_id,
          tpl.template_name,
          tpl.whatsapp_template_name,
        ];
        return refs.some((value) => ids.has(String(value || '').trim()));
      }));
    });
  }

  private loadEmailSetupHints(): void {
    this.fromSuggestions = [];
    if (this.activeChannel !== 'email' || !this.brandId) {
      return;
    }
    const found = new Set<string>();
    this.rows.forEach((row) => {
      const from = String(row.email_from || '').trim();
      if (from) {
        found.add(from);
      }
    });
    forkJoin({
      invites: this.channelSetupService.getConfig(this.brandId).pipe(catchError(() => of(null))),
      backup: this.channelSetupService.getOnDemand(this.brandId).pipe(catchError(() => of(null))),
    }).subscribe(({ invites, backup }) => {
      [invites?.channels?.email?.from, backup?.channels?.email?.from].forEach((value) => {
        const from = String(value || '').trim();
        if (from) {
          found.add(from);
        }
      });
      this.fromSuggestions = [...found];
      this.changeDetector.markForCheck();
    });
    this.fromSuggestions = [...found];
  }

  private titleCode(title: string): string {
    return title.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  private buildTemplateName(): string {
    const language = String(this.form.template_language || '').trim().toUpperCase();
    const brand4 = String(this.brandId || '').slice(-4).toUpperCase();
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const suffix = `_${language}_${brand4}_${stamp}`;
    const budget = Math.max(1, this.fieldMax.name - suffix.length);
    const code = this.titleCode(this.form.template_title).slice(0, budget);
    return `${code}${suffix}`;
  }

  private emptyForm(): CatalogForm {
    return {
      id: '',
      template_id: '',
      template_name: '',
      template_title: '',
      template_language: '',
      template_used_for: 'on_demand_request',
      template_text: '',
      sender_id: '',
      principal_entity_id: '',
      template_unicode: false,
      email_subject: '',
      email_from: '',
      template_image: '',
      is_active: true,
      html: '',
      file_name: '',
      update_log: [],
    };
  }
}
